import { UpsertVmsPayload, Vms } from '_lib/api';
import { RootState, useAppDispatch } from '_store';
import { devicesSelector } from '_store/devices/selectors';
import managePmvFormReducer, { ManagePmvForm } from '_store/forms/manage-pmv';
import { pmvsSagas } from '_store/pmv/sagas';
import { allVmsSelector } from '_store/pmv/selectors';
import { isVmsEnrolledOption, resolveRsuNames, vmsSelectLabel } from '_store/pmv/utils';
import { useLocalization } from '@fluent/react';
import { Button, CardContent, TextField, Typography } from '@material-ui/core';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { usePmvL10n } from '../../../hooks/usePmvL10n';
import { useRolePermissions } from '../../../hooks/useRolePermissions';
import MessageDialog from '../../items/MessageDialog';
import RsuMultiSelect from '../../items/RsuMultiSelect';
import { RoleGuard } from '../../items/RoleGuard';
import SelectItem from '../../items/SelectItem';
import { toLatLng } from '../../routes/MapView/utils';
import styles from './styles';
import { PmvCrudTypeEnum, PmvManageType } from './types';

const isTextValueValid = (value?: string) =>
  value !== undefined && value !== null && value.length > 0;

const isFormValid = (form: ManagePmvForm): boolean =>
  isTextValueValid(form.vmsId) && isTextValueValid(form.name) && form.rsus.length > 0;

const PmvManage: FC<PmvManageType> = ({ id, type }) => {
  const { l10n } = useLocalization();
  const { getPmvString } = usePmvL10n();
  const classes = styles();
  const dispatch = useAppDispatch();
  const { canManagePmv, blockedActionMode } = useRolePermissions();

  const allVms = useSelector(allVmsSelector);
  const devices = useSelector(devicesSelector);
  const mapRef = useSelector((state: RootState) => state.application.mapRef);
  const formState = useSelector((state: RootState) => state.forms['manage-pmv']);
  const { busy } = useSelector((state: RootState) => state.application.activeDialog);
  const writeBlockedMessage = l10n.getString('forms-rbac-readonly-action-denied');

  const isUpdate = type === PmvCrudTypeEnum.UPDATE_PMV;

  const [enrichmentPrompt, setEnrichmentPrompt] = useState<{
    active: boolean;
    vms: Vms | null;
  }>({ active: false, vms: null });

  useEffect(() => {
    dispatch(pmvsSagas.actions.vmsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (id) {
      const vms = allVms.find((item) => item.id === id);
      if (vms) {
        dispatch(
          managePmvFormReducer.actions.prefillForm({
            ...vms,
            enrichment: vms.enrichment
              ? {
                  ...vms.enrichment,
                  rsus: resolveRsuNames(vms.enrichment.rsus, devices),
                }
              : undefined,
          }),
        );
      }
    }
  }, [id, allVms, devices, dispatch]);

  const rsuOptions = useMemo(
    () => devices.map((device) => ({ id: device.name, name: device.name })),
    [devices],
  );

  const vmsSelectOptions = useMemo(
    () =>
      allVms.map((vms) => ({
        label: isVmsEnrolledOption(vms)
          ? `${vmsSelectLabel(vms)} ${l10n.getString('forms-pmv-vms-enrolled-suffix')}`
          : vmsSelectLabel(vms),
        value: vms.id,
        enrolled: isVmsEnrolledOption(vms),
      })),
    [allVms, l10n],
  );

  const submitForm = () => {
    const payload: UpsertVmsPayload = {
      name: formState.name.trim(),
      rsus: formState.rsus,
    };

    dispatch(
      pmvsSagas.actions.upsertVms({
        id: formState.vmsId,
        vmsPayload: payload,
        isCreate: !isUpdate,
      }),
    );

    const vms = allVms.find((item) => item.id === formState.vmsId);
    if (vms && mapRef) {
      mapRef.panTo(toLatLng([vms.longitude, vms.latitude]));
    }
  };

  const applyVmsSelection = useCallback(
    (vmsId: string, vms: Vms | undefined) => {
      dispatch(managePmvFormReducer.actions.vmsIdChanged(vmsId));
      if (vms && isVmsEnrolledOption(vms)) {
        setEnrichmentPrompt({ active: true, vms });
      }
    },
    [dispatch],
  );

  const onChangeVmsId = useCallback(
    (ev: React.ChangeEvent<{ value: unknown }>) => {
      const vmsId = String(ev.target.value);
      const vms = allVms.find((item) => item.id === vmsId);
      applyVmsSelection(vmsId, vms);
    },
    [allVms, applyVmsSelection],
  );

  const onEnrichmentPromptClose = (choice: 'cancel' | 'confirm') => {
    if (choice === 'confirm' && enrichmentPrompt.vms?.enrichment) {
      dispatch(
        managePmvFormReducer.actions.prefillEnrichment({
          name: enrichmentPrompt.vms.enrichment.name,
          rsus: resolveRsuNames(enrichmentPrompt.vms.enrichment.rsus, devices),
        }),
      );
    }
    setEnrichmentPrompt({ active: false, vms: null });
  };

  const onChangeName = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(managePmvFormReducer.actions.nameChanged(ev.target.value));
    },
    [dispatch],
  );

  const onRsusChange = useCallback(
    (selectedIds: string[]) => {
      dispatch(managePmvFormReducer.actions.rsusChanged(selectedIds));
    },
    [dispatch],
  );

  return (
    <CardContent className={classes.cardContent}>
      {enrichmentPrompt.active ? (
        <MessageDialog
          statusMessage={{ active: true, typeAction: 'pmv-load-enrichment' }}
          onCloseDialog={onEnrichmentPromptClose}
        />
      ) : null}

      <SelectItem
        text={l10n.getString('forms-pmv-id')}
        options={vmsSelectOptions}
        value={formState.vmsId}
        onChange={onChangeVmsId}
        disabled={busy || isUpdate}
        mandatory
        formControlStyle={{ marginTop: 10, width: '100%' }}
      />

      <TextField
        id="pmv-name"
        label={l10n.getString('forms-pmv-name')}
        InputLabelProps={{ style: { fontSize: 12 } }}
        value={formState.name}
        disabled={busy}
        onChange={onChangeName}
        fullWidth
        className={classes.input}
      />
      <div className={classes.rsuSelect}>
        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
          {l10n.getString('forms-pmv-rsus')}
        </Typography>
        <RsuMultiSelect
          options={rsuOptions}
          selectedIds={formState.rsus}
          onChange={onRsusChange}
          disabled={busy}
          placeholder={l10n.getString('forms-pmv-rsus-placeholder')}
        />
      </div>

      <RoleGuard
        allowed={canManagePmv}
        blockedActionMode={blockedActionMode}
        blockedMessage={writeBlockedMessage}
      >
        <Button
          variant="outlined"
          className={classes.submitButton}
          disabled={!isFormValid(formState) || busy}
          onClick={submitForm}
          fullWidth
        >
          {isUpdate ? getPmvString('forms-pmv-update') : getPmvString('forms-pmv-add-new')}
        </Button>
      </RoleGuard>
    </CardContent>
  );
};

export default React.memo(PmvManage);
